import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { useSearchParamsStore } from '@aibees/elephant/store/search-params-store';
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import { useEffect, useMemo, useState } from 'react';
import { Provider, DataViewChartProvider } from './context';
import { DataViewContextProps as ContextProps, View, ViewType } from './types';

const VALID_SCOPES: SearchTableScope[] = ['company', 'people', 'employees'];

function isValidScope(
  scope: SearchTableScope | undefined
): scope is SearchTableScope {
  return scope != null && VALID_SCOPES.includes(scope);
}

export const DataViewProvider = ({ children, value }: ContextProps) => {
  const scope = useSearchTableScope();
  const { params, getParam } = useSearchQueryFilter({ select: ['view'] });
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();

  const initialView = getParam('view') === 'chart' ? 'CHART' : 'TABLE';
  const [viewType, setViewType] = useState<ViewType>(initialView);

  const handleSwitchView = (item: View) => {
    if (item.comingSoon) return;
    if (!isValidScope(scope)) return;

    const newType = item.type.toLowerCase();
    setViewType(item.type);

    const fullParams =
      useSearchParamsStore.getState().paramsByScope[scope] ?? {};
    const updatedParams = { ...fullParams };
    if (newType === 'chart') {
      updatedParams.view = 'chart';
    }
    if (newType === 'table') {
      delete updatedParams.view;
    }
    replaceParamsForScope(scope, updatedParams);
  };

  useEffect(() => {
    const paramView = params.view;
    if (!isValidScope(scope)) return;

    const fullParams =
      useSearchParamsStore.getState().paramsByScope[scope] ?? {};
    const hasActiveFilters = Object.keys(fullParams).some(
      (key) => !['view', 'page', 'size'].includes(key) && fullParams[key]
    );
    if (paramView === 'chart' && !hasActiveFilters) {
      const updatedParams = { ...fullParams };
      delete updatedParams.view;
      replaceParamsForScope(scope, updatedParams);
      setViewType('TABLE');
      return;
    }
    if (paramView === 'chart' && viewType !== 'CHART') setViewType('CHART');
    if (!paramView && viewType !== 'TABLE') setViewType('TABLE');
  }, [params, scope, viewType, replaceParamsForScope]);

  const viewItems: View[] = [
    {
      label: 'Table',
      type: 'TABLE',
      icon: 'table',
      route: '',
      disabled: false,
      selected: viewType === 'TABLE',
      comingSoon: false,
    },
    {
      label: 'Charts',
      type: 'CHART',
      icon: 'chart',
      route: '',
      disabled: true,
      selected: viewType === 'CHART',
      comingSoon: false,
    },
  ];

  const isChartView = viewType === 'CHART';
  const chartContextValue = useMemo(() => ({ isChartView }), [isChartView]);

  return (
    <Provider value={{ viewItems, handleSwitchView, isChartView }}>
      <DataViewChartProvider value={chartContextValue}>
        {children}
      </DataViewChartProvider>
    </Provider>
  );
};
